export type AdminResourceScope = {
  accountId: string;
  storeId: string;
};

type CacheEntry = {
  value: unknown;
  loadedAt: string;
};

export type AdminResourceStatus = "idle" | "loading" | "refreshing" | "ready" | "error";

export type AdminResourceLoadOptions = {
  cache?: AdminResourceCache;
  cacheKey?: string;
  force?: boolean;
};

export class AdminResourceController<T> {
  value: T | undefined;
  status: AdminResourceStatus = "idle";
  error: unknown = null;
  lastLoadedAt: string | null = null;
  private requestId = 0;
  private abortController: AbortController | undefined;
  private activeKey: string | undefined;

  setCached(value: T, loadedAt: string, key?: string): void {
    this.activeKey = key;
    this.value = value;
    this.lastLoadedAt = loadedAt;
    this.status = "ready";
    this.error = null;
  }

  async load(key: string | undefined, loader: (signal: AbortSignal) => Promise<T>, options: AdminResourceLoadOptions = {}): Promise<T | undefined> {
    this.prepareKey(key);
    if (!options.force && key && options.cache) {
      const cached = options.cache.read<T>(key);
      if (cached) {
        this.setCached(cached.value, cached.loadedAt, key);
        return cached.value;
      }
    }
    return this.reload(loader, { ...options, cacheKey: key });
  }

  async reload(loader: (signal: AbortSignal) => Promise<T>, options: AdminResourceLoadOptions = {}): Promise<T | undefined> {
    this.prepareKey(options.cacheKey);
    const requestId = ++this.requestId;
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;
    this.status = this.value === undefined ? "loading" : "refreshing";
    this.error = null;
    try {
      const next = await loader(abortController.signal);
      if (requestId !== this.requestId) return this.value;
      const loadedAt = new Date().toISOString();
      this.value = next;
      this.lastLoadedAt = loadedAt;
      this.status = "ready";
      if (options.cache && options.cacheKey) options.cache.write(options.cacheKey, next, loadedAt);
      return next;
    } catch (error) {
      if (requestId !== this.requestId || (error instanceof DOMException && error.name === "AbortError")) return this.value;
      this.error = error;
      this.status = "error";
      throw error;
    } finally {
      if (requestId === this.requestId) this.abortController = undefined;
    }
  }

  clear(): void {
    this.requestId += 1;
    this.abortController?.abort();
    this.abortController = undefined;
    this.value = undefined;
    this.error = null;
    this.lastLoadedAt = null;
    this.status = "idle";
    this.activeKey = undefined;
  }

  private prepareKey(key: string | undefined): void {
    if (this.activeKey === key) return;
    this.requestId += 1;
    this.abortController?.abort();
    this.abortController = undefined;
    this.value = undefined;
    this.error = null;
    this.lastLoadedAt = null;
    this.status = "idle";
    this.activeKey = key;
  }
}

function normalizePart(value: string): string {
  return encodeURIComponent(value.trim());
}

export function adminResourceKey(scope: AdminResourceScope, resource: string, query = ""): string {
  return [normalizePart(scope.accountId), normalizePart(scope.storeId), normalizePart(resource), normalizePart(query)].join("/");
}

export class AdminResourceCache {
  private readonly entries = new Map<string, CacheEntry>();

  read<T>(key: string): { value: T; loadedAt: string } | undefined {
    const entry = this.entries.get(key);
    return entry ? { value: entry.value as T, loadedAt: entry.loadedAt } : undefined;
  }

  write<T>(key: string, value: T, loadedAt = new Date().toISOString()): void {
    this.entries.set(key, { value, loadedAt });
  }

  clear(): void {
    this.entries.clear();
  }

  clearScope(scope: AdminResourceScope): void {
    const prefix = `${normalizePart(scope.accountId)}/${normalizePart(scope.storeId)}/`;
    for (const key of this.entries.keys()) if (key.startsWith(prefix)) this.entries.delete(key);
  }

  size(): number {
    return this.entries.size;
  }
}

export const adminResourceCache = new AdminResourceCache();
